// R115 guest — the form sheets behind the reservation / listing actions:
// Resolution (B1), Claim (B3), Recovery (B2) and Message (B5). Each opens in
// the calendar's form sheet host (ctx.openSheet — never redrawn by timeline or
// inspector redraws), keeps its own state in this module (typing is never lost
// to a redraw: only status / thread regions are patched, never the inputs),
// reports dirtiness through ctx.markDirty and the 'guest' busy hook, and
// writes ONLY through the owning module's existing route tagged
// source:'calendar'. Message text and phone numbers live in memory while the
// sheet is open and are dropped on close — never in sessionStorage.
import type { CalendarCtx } from '../registry';
import { resolutionBody, claimBody, RES_REASONS, RES_CHANNELS, RES_CHANNEL_LABEL, resChannelOf, threadView, composerState, maybeSent } from '../../../../../lib/calendarActions.mjs';
import { STATE_LABEL, STATE_PILL, isClosed } from '../../../../../lib/guestRecovery.mjs';
import type { RecoveryOutcome } from '../../../../../lib/guestRecovery.mjs';
import { recoveryComposerHtml, recoveryTimelineHtml, composerGuard } from '../../../shared/components/RecoveryComposer';
import { PmsSendConfirm } from '../../../shared/components/PmsSendConfirm';
import { bindKebabs, closeKebab } from '../../../shared/components/Kebab';
import { listingCards, recoveryTargets, convRefs, getJson, postJson, errText, actorEmail, handoff, fmtWhen, money } from './util';
import { dropPreview } from './sections';
import type { RecoveryTarget } from './util';

// ── the one open sheet ──────────────────────────────────────────────────
interface SheetState { kind: string; key: string; host: HTMLElement | null; dirty: boolean; busy: boolean; closed: boolean; cleanup: (() => void)[] }
let current: SheetState | null = null;
export const sheetBusy = (): boolean => !!current && !current.closed && (current.busy || current.dirty);
export const openSheetKind = (): string | null => (current && !current.closed ? `${current.kind}:${current.key}` : null);

function startSheet(ctx: CalendarCtx, kind: string, key: string, title: string, html: string): SheetState {
  const st: SheetState = { kind, key, host: null, dirty: false, busy: false, closed: false, cleanup: [] };
  const finish = () => {
    if (st.closed) return;
    st.closed = true;
    for (const f of st.cleanup) { try { f(); } catch { /* best effort */ } }
    if (current === st) current = null;
    ctx.markDirty(false);
  };
  st.cleanup.push(() => closeKebab());
  current = st;
  st.host = ctx.openSheet(html, { title, onClose: finish });
  (st as any).finish = finish;
  return st;
}
function closeSheet(ctx: CalendarCtx, st: SheetState): void {
  ctx.closeSheet();
  const f = (st as any).finish as (() => void) | undefined;
  if (f) f();
}
function setDirty(ctx: CalendarCtx, st: SheetState, dirty: boolean): void {
  if (st.closed || st.dirty === dirty) return;
  st.dirty = dirty;
  ctx.markDirty(dirty || st.busy);
}
const $ = <T extends HTMLElement = HTMLElement>(root: HTMLElement | null, sel: string): T | null => (root ? root.querySelector<T>(sel) : null);
const profileName = (): string => { try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String((p && p.full_name) || '').trim(); } catch { return ''; } };
function note(root: HTMLElement | null, text: string, tone: 'ok' | 'bad' | 'warn' = 'bad'): void {
  const el = $(root, '[data-gx-note]');
  if (!el) return;
  el.className = `gx-note ${tone}`;
  el.textContent = text;
  el.hidden = !text;
}
const stayLine = (ctx: CalendarCtx, ci?: string, co?: string) => (ci && co ? `${ctx.fmt.md(ci)} → ${ctx.fmt.md(co)}` : '');

// ════════════════════════════════════════════════════════════════════════
// B1 — Resolution (refund to the guest / extra charge), from a stay or a listing
// ════════════════════════════════════════════════════════════════════════
export interface ResTarget { listing_id: string; listing_name: string; reservation_id?: string | null; confirmation_code?: string | null; guest?: string; channel?: string; check_in?: string; check_out?: string }

export function openResolutionSheet(ctx: CalendarCtx, t: ResTarget): void {
  const esc = ctx.esc;
  const f = { direction: 'refund', amount: '', reason_code: 'issue', reason: '', mode: 'us', owner_pct: '50', channel: t.channel ? resChannelOf(t.channel) : 'airbnb', guest: t.guest || '', link: '', date: ctx.asOf };
  let owner: { id: string; name: string } | null = null;
  let ownerKnown = false;
  let ownerFailed = false;   // R115 review: a failed lookup is not "no owner assigned"
  // R115 review: this sheet's idempotency key — a retry of the SAME save is one row; a second refund asks
  const clientRef = (() => { try { return crypto.randomUUID(); } catch { return `cal-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`; } })();
  const seg = (attr: string, opts: [string, string][], val: string) => `<div class="seg-tabs gx-seg" role="tablist">${opts.map(([v, l]) => `<button type="button" class="seg-tab ${v === val ? 'active' : ''}" ${attr}="${v}" role="tab" aria-selected="${v === val}">${esc(l)}</button>`).join('')}</div>`;
  const html = `
    <div class="gx-sheet gx-res" data-gx-sheet="resolution">
      <div class="gx-ctx">🗓 <b>${esc(t.listing_name)}</b>${t.guest ? ` · ${esc(t.guest)}` : ''}${t.check_in ? ` · ${esc(stayLine(ctx, t.check_in, t.check_out))}` : ''}${t.confirmation_code ? ` · <span class="mono">${esc(t.confirmation_code)}</span>` : ''}</div>
      <div class="gx-field"><span class="gx-lab">Type</span>${seg('data-gx-dir', [['refund', 'Refund to guest'], ['charge', 'Extra charge']], f.direction)}</div>
      <div class="gx-grid2">
        <label class="gx-field"><span class="gx-lab">Amount ($)</span><input class="input" data-gx-f="amount" inputmode="decimal" autocomplete="off" placeholder="150"></label>
        <label class="gx-field"><span class="gx-lab">Reason</span><select class="input" data-gx-f="reason_code">${RES_REASONS.map(([v, l]) => `<option value="${v}" ${v === f.reason_code ? 'selected' : ''}>${esc(l)}</option>`).join('')}</select></label>
      </div>
      <label class="gx-field"><span class="gx-lab">Description</span><input class="input" data-gx-f="reason" autocomplete="off" placeholder="e.g. Late checkout comp / broken AC credit"></label>
      <div class="gx-field"><span class="gx-lab">Attribute to</span>${seg('data-gx-mode', [['us', 'Us (company)'], ['owner', 'Owner'], ['split', 'Split %']], f.mode)}
        <div class="gx-owner" data-gx-owner hidden></div>
        <label class="gx-field" data-gx-split hidden><span class="gx-lab">Owner's share (%)</span><input class="input" data-gx-f="owner_pct" inputmode="numeric" value="${esc(f.owner_pct)}"></label>
      </div>
      <div class="gx-grid2">
        <label class="gx-field"><span class="gx-lab">Guest</span><input class="input" data-gx-f="guest" autocomplete="off" value="${esc(f.guest)}" placeholder="Guest name"></label>
        <label class="gx-field"><span class="gx-lab">Booking channel</span><select class="input" data-gx-f="channel">${(RES_CHANNELS as string[]).map(c => `<option value="${c}" ${c === f.channel ? 'selected' : ''}>${esc((RES_CHANNEL_LABEL as any)[c])}</option>`).join('')}</select></label>
      </div>
      <label class="gx-field"><span class="gx-lab">Link (optional)</span><input class="input" type="url" data-gx-f="link" inputmode="url" autocomplete="off" spellcheck="false" placeholder="https://www.airbnb.com/resolutions/…"></label>
      <div class="gx-fine">Taxable / commissionable follow the ledger's rules (auto) — change them on the Resolutions page if needed. Track-and-report only: never sent to owner statements.</div>
      <div class="gx-note" data-gx-note hidden></div>
      <div class="gx-foot" data-bottom-bar>
        <button type="button" class="btn btn-ghost" data-gx-cancel>Cancel</button>
        <button type="button" class="btn btn-primary" data-gx-save>Save resolution</button>
      </div>
    </div>`;
  const st = startSheet(ctx, 'resolution', t.reservation_id || t.listing_id, t.reservation_id ? 'Log a resolution' : `Log a resolution · ${t.listing_name}`, html);
  const root = st.host;
  if (!root) return;
  const ownerLine = () => {
    const el = $(root, '[data-gx-owner]');
    const sp = $(root, '[data-gx-split]');
    if (sp) sp.hidden = f.mode !== 'split';
    if (!el) return;
    el.hidden = f.mode === 'us';
    el.textContent = !ownerKnown ? 'Owner: looking up…' : owner ? `Owner: ${owner.name || owner.id}`
      : ownerFailed ? "Couldn't look up the owner — it saves without one (set it on the Resolutions page)."
      : 'No owner is assigned to this home — it saves without one (set it on the Resolutions page).';
  };
  const lookupOwner = () => listingCards.get(t.listing_id).then(card => { owner = card && card.owner ? card.owner : null; ownerFailed = !card; ownerKnown = true; if (!st.closed) ownerLine(); });
  const ownerP = lookupOwner();
  const dirtyNow = () => setDirty(ctx, st, !!(f.amount.trim() || f.reason.trim() || f.link.trim()));
  root.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-gx-f]').forEach(el => {
    const k = el.dataset.gxF as keyof typeof f;
    const upd = () => { (f as any)[k] = el.value; dirtyNow(); };
    el.addEventListener('input', upd); el.addEventListener('change', upd);
  });
  const segBind = (attr: string, key: 'direction' | 'mode') => root.querySelectorAll<HTMLElement>(`[${attr}]`).forEach(b => b.addEventListener('click', () => {
    (f as any)[key] = b.getAttribute(attr) || '';
    root.querySelectorAll<HTMLElement>(`[${attr}]`).forEach(x => { const on = x === b; x.classList.toggle('active', on); x.setAttribute('aria-selected', String(on)); });
    ownerLine();
  }));
  segBind('data-gx-dir', 'direction'); segBind('data-gx-mode', 'mode');
  ownerLine();
  $(root, '[data-gx-cancel]')?.addEventListener('click', () => closeSheet(ctx, st));
  const save = $<HTMLButtonElement>(root, '[data-gx-save]');
  save?.addEventListener('click', async () => {
    if (st.busy) return;
    // R115 review: an Owner / Split save waits for the owner lookup (it used to go out with owner_id null mid-lookup)
    if (f.mode !== 'us' && !ownerKnown) { st.busy = true; save.disabled = true; save.textContent = 'Saving…'; await ownerP; st.busy = false; save.disabled = false; save.textContent = 'Save resolution'; if (st.closed) return; }
    const v = resolutionBody(f, { listing_id: t.listing_id, listing_name: t.listing_name, owner_id: owner ? owner.id : null, reservation_id: t.reservation_id || null, confirmation_code: t.confirmation_code || null, check_in: t.check_in, check_out: t.check_out, fmt: ctx.fmt.md, client_ref: clientRef });
    if (!v.ok) { note(root, v.error || 'Check the form.'); return; }
    st.busy = true; save.disabled = true; save.textContent = 'Saving…'; note(root, '');
    const r = await ctx.postWithOverride('/api/resolutions', v.body as Record<string, unknown>);
    st.busy = false;
    if (st.closed) return;
    if (!r.ok) {
      save.disabled = false; save.textContent = 'Save resolution';
      // a declined "Log another one?" (409 needs_confirm) is the user's choice, not an error
      if (r.status === 409 && r.json && (r.json as any).needs_confirm) { note(root, 'Not saved — the same amount is already logged for this stay. Change it, or save again and choose to log another.', 'warn'); return; }
      note(root, errText({ status: r.status, json: r.json || (r.error ? { error: r.error } : null) }, "Didn't save"));
      return;
    }
    const row = (r.json && r.json.resolution) || {};
    listingCards.drop(t.listing_id);
    setDirty(ctx, st, false);
    const already = !!(r.json && r.json.already);
    const sheet = $(root, '.gx-sheet') || root;
    sheet.innerHTML = `
      <div class="gx-done">
        <div class="gx-big">${already ? 'Already in the ledger' : '✓ Resolution logged'}</div>
        <div class="gx-sub">${already ? 'This resolution was already saved (the first tap went through) — nothing was added twice.' : `${esc(money(row.amount))} ${row.direction === 'charge' ? 'extra charge' : 'refund'} · ${esc(row.attribution && row.attribution.mode === 'split' ? `split (owner ${row.attribution.owner_pct}%)` : row.attribution && row.attribution.mode === 'owner' ? 'owner' : 'us')}${row.reason ? ` · ${esc(row.reason)}` : ''}`}</div>
      </div>
      <div class="gx-foot" data-bottom-bar>
        <button type="button" class="btn btn-secondary gx-foot-left" data-gx-open>Open in Resolutions</button>
        <button type="button" class="btn btn-primary" data-gx-done>Done</button>
      </div>`;
    $(sheet, '[data-gx-open]')?.addEventListener('click', () => { closeSheet(ctx, st); handoff.resolutions(ctx); });
    $(sheet, '[data-gx-done]')?.addEventListener('click', () => closeSheet(ctx, st));
    ctx.toast(already ? 'Already in the Resolutions ledger.' : 'Resolution logged.');
    void ctx.reload({ inspector: true });
  });
  $<HTMLInputElement>(root, '[data-gx-f="amount"]')?.focus();
}

// ════════════════════════════════════════════════════════════════════════
// B3 — Damage / claim, from a stay or a listing
// ════════════════════════════════════════════════════════════════════════
export interface ClaimTarget { listing_id: string; listing_name: string; reservation_id?: string | null; guest?: string; check_in?: string; check_out?: string }

export function openClaimSheet(ctx: CalendarCtx, t: ClaimTarget): void {
  const esc = ctx.esc;
  const f = { title: '', note: '', amount: '', guest: t.guest || '' };
  const html = `
    <div class="gx-sheet gx-claim" data-gx-sheet="claim">
      <div class="gx-ctx">🗓 <b>${esc(t.listing_name)}</b>${t.guest ? ` · ${esc(t.guest)}` : ''}${t.check_in ? ` · ${esc(stayLine(ctx, t.check_in, t.check_out))}` : ''}</div>
      <label class="gx-field"><span class="gx-lab">What was damaged? *</span><input class="input" data-gx-f="title" autocomplete="off" maxlength="200" placeholder="e.g. Broken lamp in the living room"></label>
      <label class="gx-field"><span class="gx-lab">What happened</span><textarea class="input" data-gx-f="note" rows="3" maxlength="4000" placeholder="Found by the cleaner at checkout; photos to follow…"></textarea></label>
      <div class="gx-grid2">
        <label class="gx-field"><span class="gx-lab">Amount ($, optional)</span><input class="input" data-gx-f="amount" inputmode="decimal" autocomplete="off" placeholder="85"></label>
        <label class="gx-field"><span class="gx-lab">Guest</span><input class="input" data-gx-f="guest" autocomplete="off" value="${esc(f.guest)}" placeholder="Guest name"></label>
      </div>
      <div class="gx-fine">Photos, the recovery channel (waiver / Waivo / Airbnb) and the status live on the Claims page — open it after filing. Internal only: never on owner statements.</div>
      <div class="gx-note" data-gx-note hidden></div>
      <div class="gx-foot" data-bottom-bar>
        <button type="button" class="btn btn-ghost" data-gx-cancel>Cancel</button>
        <button type="button" class="btn btn-primary" data-gx-save>File claim</button>
      </div>
    </div>`;
  const st = startSheet(ctx, 'claim', t.reservation_id || t.listing_id, t.reservation_id ? 'File a damage claim' : `File a damage claim · ${t.listing_name}`, html);
  const root = st.host;
  if (!root) return;
  root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('[data-gx-f]').forEach(el => {
    const k = el.dataset.gxF as keyof typeof f;
    el.addEventListener('input', () => { (f as any)[k] = el.value; setDirty(ctx, st, !!(f.title.trim() || f.note.trim() || f.amount.trim())); });
  });
  $(root, '[data-gx-cancel]')?.addEventListener('click', () => closeSheet(ctx, st));
  const save = $<HTMLButtonElement>(root, '[data-gx-save]');
  save?.addEventListener('click', async () => {
    if (st.busy) return;
    const v = claimBody(f, { listing_id: t.listing_id, listing_name: t.listing_name, reservation_id: t.reservation_id || null, day: ctx.asOf, by: actorEmail() || ctx.actorName() });
    if (!v.ok) { note(root, v.error || 'Check the form.'); $<HTMLInputElement>(root, '[data-gx-f="title"]')?.focus(); return; }
    st.busy = true; save.disabled = true; save.textContent = 'Filing…'; note(root, '');
    const r = await ctx.postWithOverride('/api/assetops/claims', v.body as Record<string, unknown>);
    st.busy = false;
    if (st.closed) return;
    if (!r.ok) {
      save.disabled = false; save.textContent = 'File claim';
      note(root, errText({ status: r.status, json: r.json || (r.error ? { error: r.error } : null) }, "Didn't file"));
      return;
    }
    listingCards.drop(t.listing_id);
    setDirty(ctx, st, false);
    const already = !!(r.json && r.json.already);
    const c = (r.json && r.json.claim) || {};
    const sheet = $(root, '.gx-sheet') || root;
    sheet.innerHTML = `
      <div class="gx-done">
        <div class="gx-big">${already ? 'Already filed' : '✓ Claim filed'}</div>
        <div class="gx-sub">${already ? `“${esc(c.title || f.title)}” is already filed for this ${t.reservation_id ? 'stay' : 'home today'} (${esc(c.status || 'open')}) — nothing was added.` : `${esc(c.title || f.title)}${c.amount != null ? ` · ${esc(money(c.amount))}` : ''} · open`}</div>
        <div class="gx-faint">Add photos and pick how it's recovered on the Claims page.</div>
      </div>
      <div class="gx-foot" data-bottom-bar>
        <button type="button" class="btn btn-secondary gx-foot-left" data-gx-open>Open Claims (add photos)</button>
        <button type="button" class="btn btn-primary" data-gx-done>Done</button>
      </div>`;
    $(sheet, '[data-gx-open]')?.addEventListener('click', () => { closeSheet(ctx, st); handoff.claims(ctx); });
    $(sheet, '[data-gx-done]')?.addEventListener('click', () => closeSheet(ctx, st));
    ctx.toast(already ? 'That claim was already filed.' : 'Claim filed.');
    void ctx.reload({ inspector: true });
  });
  $<HTMLInputElement>(root, '[data-gx-f="title"]')?.focus();
}

// ════════════════════════════════════════════════════════════════════════
// B2 — Guest Recovery for a stay (the shared composer)
// ════════════════════════════════════════════════════════════════════════
export interface RecTarget { hid: string; guest: string; listing_name: string; check_in?: string; check_out?: string }

export function openRecoverySheet(ctx: CalendarCtx, t: RecTarget): void {
  const esc = ctx.esc;
  const s = { loading: true, error: '', target: null as RecoveryTarget | null, draft: '', err: '', busy: false };
  const html = `
    <div class="gx-sheet gx-rec" data-gx-sheet="recovery">
      <div class="gx-ctx">❤ <b>${esc(t.guest || 'Guest')}</b> · ${esc(t.listing_name)}${t.check_in ? ` · ${esc(stayLine(ctx, t.check_in, t.check_out))}` : ''}</div>
      <div class="gx-rec-body" data-gx-rec-body><div class="gx-load">Loading the recovery case…</div></div>
      <div class="gx-foot" data-bottom-bar>
        <button type="button" class="btn btn-ghost gx-foot-left" data-gx-gr-open>Open in Guest Recovery</button>
        <button type="button" class="btn btn-secondary" data-gx-cancel>Close</button>
      </div>
    </div>`;
  const st = startSheet(ctx, 'recovery', t.hid, 'Guest recovery', html);
  const root = st.host;
  if (!root) return;
  const fmtT = (x: unknown) => fmtWhen(x);
  const draw = () => {
    if (st.closed) return;
    const body = $(root, '[data-gx-rec-body]');
    if (!body) return;
    closeKebab();
    const keepFocus = document.activeElement && (document.activeElement as HTMLElement).dataset && (document.activeElement as HTMLElement).dataset.role === 'gr-note';
    if (s.loading) body.innerHTML = '<div class="gx-load">Loading the recovery case…</div>';
    else if (s.error) body.innerHTML = `<div class="gx-err" role="alert">${esc(s.error)}</div><div class="gx-btnrow"><button type="button" class="btn btn-secondary" data-gx-retry>↻ Retry</button></div>`;
    else if (!s.target) {
      body.innerHTML = `
        <div class="gx-sub">No recovery case for this stay yet. Write what happened and what you did — it starts a Guest Recovery case (and takes over this guest's message thread when there is one), so the team sees it on the Guest Recovery list.</div>
        <label class="gr-notelab">What happened?
          <textarea class="input" data-role="gr-note" rows="3" maxlength="1000" placeholder="Guest says the hot tub is cold; offered a late checkout…">${esc(s.draft)}</textarea>
        </label>
        ${s.err ? `<div class="gr-err" role="alert">${esc(s.err)}</div>` : ''}
        <div class="gx-btnrow"><button type="button" class="btn btn-primary gr-btn" data-gx-start ${s.busy ? 'disabled' : ''}>${s.busy ? 'Starting…' : 'Start a recovery case'}</button></div>`;
    } else {
      const tg = s.target;
      const state = (tg.recovery_state || 'open') as keyof typeof STATE_LABEL;
      const closed = isClosed(tg);
      body.innerHTML = `
        <div class="gx-rec-head"><span class="pill ${STATE_PILL[state] || 'pill-red'}">${esc(STATE_LABEL[state] || 'Open')}</span>
          <span class="gx-sub">${tg.kind === 'case' ? `Recovery case${tg.origin === 'calendar' ? ' · started from the Calendar' : tg.origin === 'issues-board' ? ' · from the Issues board' : ''}` : 'Guest message thread on the recovery list'}</span></div>
        ${tg.summary ? `<div class="gx-t">${esc(tg.summary)}</div>` : ''}
        ${tg.excerpt ? `<div class="gx-sub">“${esc(tg.excerpt)}”</div>` : ''}
        <div class="gx-lab">Activity</div>
        ${recoveryTimelineHtml(tg as any, { esc, fmtT })}
        ${recoveryComposerHtml({ closed, state: state as any, draft: s.draft, err: s.err, busy: s.busy, smoothedAt: tg.smoothed_at || null, esc, fmtT })}`;
    }
    bindBody(body);
    if (keepFocus) { const ta = $<HTMLTextAreaElement>(body, '[data-role="gr-note"]'); if (ta) { ta.focus(); ta.setSelectionRange(ta.value.length, ta.value.length); } }
  };
  const bindBody = (body: HTMLElement) => {
    bindKebabs(body);
    const ta = $<HTMLTextAreaElement>(body, '[data-role="gr-note"]');
    ta?.addEventListener('input', () => { s.draft = ta.value; if (s.err && s.draft.trim()) { s.err = ''; body.querySelector('.gr-err')?.remove(); } setDirty(ctx, st, !!s.draft.trim()); });
    $(body, '[data-gx-retry]')?.addEventListener('click', () => void load());
    $(body, '[data-gx-start]')?.addEventListener('click', () => void start());
    body.querySelectorAll<HTMLElement>('[data-act]').forEach(b => b.addEventListener('click', () => {
      void act(b.dataset.act as 'log' | 'close' | 'reopen', (b.dataset.outcome || undefined) as RecoveryOutcome | undefined);
    }));
  };
  const load = async () => {
    s.loading = true; s.error = ''; draw();
    recoveryTargets.drop(t.hid);
    const j = await recoveryTargets.get(t.hid);
    s.loading = false;
    if (!j) s.error = errText({ status: recoveryTargets.lastStatus(t.hid), json: null }, "Couldn't load the recovery case");
    else s.target = j.target;
    draw();
  };
  const by = () => ({ by: actorEmail() || undefined, by_name: profileName() || ctx.actorName() || undefined });
  const start = async () => {
    if (s.busy) return;
    const note = s.draft.trim();
    if (!note) { s.err = 'Write what happened first.'; draw(); return; }
    s.busy = true; st.busy = true; s.err = ''; draw();
    const r = await ctx.postWithOverride('/api/assetops/issues/guest-recovery/from-reservation', { reservation_id: t.hid, note, ...by() });
    s.busy = false; st.busy = false;
    if (st.closed) return;
    if (!r.ok) { s.err = errText({ status: r.status, json: r.json || (r.error ? { error: r.error } : null) }, "Didn't start the case"); draw(); return; }
    s.target = (r.json && r.json.recovery) || null;
    s.draft = ''; setDirty(ctx, st, false);
    recoveryTargets.drop(t.hid);
    ctx.toast(r.json && r.json.merged_into_existing ? 'Added to the open recovery case.' : 'Recovery case started.');
    draw();
    void ctx.reload({ inspector: true });
  };
  const act = async (op: 'log' | 'close' | 'reopen', outcome?: RecoveryOutcome) => {
    if (s.busy || !s.target) return;
    closeKebab();
    const text = s.draft.trim();
    const guard = composerGuard(op, outcome, text);
    if (guard) { s.err = guard; draw(); $<HTMLTextAreaElement>(root, '[data-role="gr-note"]')?.focus(); return; }
    s.busy = true; st.busy = true; s.err = ''; draw();
    const r = await ctx.postWithOverride(`/api/assetops/issues/guest-recovery/${encodeURIComponent(s.target.id)}`, { op, ...(op === 'close' ? { outcome } : {}), text, source: 'calendar', ...by() });
    s.busy = false; st.busy = false;
    if (st.closed) return;
    if (!r.ok) { s.err = errText({ status: r.status, json: r.json || (r.error ? { error: r.error } : null) }, "Didn't save — your note is still in the box"); draw(); return; }
    if (r.json && r.json.recovery) Object.assign(s.target, r.json.recovery);
    s.draft = ''; setDirty(ctx, st, false);
    recoveryTargets.drop(t.hid);
    const label = STATE_LABEL[(s.target.recovery_state || 'open') as keyof typeof STATE_LABEL];
    ctx.toast(r.json && r.json.unchanged ? `Already ${label} — nothing changed.` : op === 'log' ? 'Logged.' : op === 'reopen' ? 'Reopened.' : `Closed as ${label}.`);
    draw();
    void ctx.reload({ inspector: true });
  };
  $(root, '[data-gx-cancel]')?.addEventListener('click', () => closeSheet(ctx, st));
  $(root, '[data-gx-gr-open]')?.addEventListener('click', () => { const id = s.target ? s.target.id : null; closeSheet(ctx, st); handoff.recovery(ctx, id); });
  void load();
}

// ════════════════════════════════════════════════════════════════════════
// B5 — Message the guest (the reservation's PMS thread, or SMS) + read it
// ════════════════════════════════════════════════════════════════════════
export interface MsgTarget { hid: string; guest: string; listing_name: string; check_in?: string; check_out?: string; channel?: string; cidHint?: string | null }
interface ByRes { conversation_id: string | null; source: string | null; lookup_error?: string | null; configured: { sms: boolean; pms: boolean }; pms_send: boolean; guest: { name: string; phone: string | null }; reservation: any; property: { name: string } }
interface QuoLine { id: string; number: string; name: string }

const pmsConfirm = new PmsSendConfirm();
/** "+1 (555) 123-4567" → "•••4567" — the sheet never shows the guest's full number. */
const maskPhone = (p: unknown): string => { const d = String(p || '').replace(/\D/g, ''); return d.length >= 4 ? `•••${d.slice(-4)}` : 'the guest'; };
export function openMessageSheet(ctx: CalendarCtx, t: MsgTarget): void {
  const esc = ctx.esc;
  const chanLabel = resChannelOf(t.channel) === 'airbnb' ? 'Airbnb' : resChannelOf(t.channel) === 'vrbo' ? 'VRBO' : resChannelOf(t.channel) === 'booking' ? 'Booking.com' : 'booking';
  // in-memory only, dropped on close
  const s = {
    loading: true, error: '', by: null as ByRes | null,
    tab: 'pms' as 'pms' | 'sms',
    // fresh = no Quo thread with this guest yet: the first text starts one (R115 review B5)
    sms: null as null | { phone_number_id: string; participants: string[]; line: string; fresh?: boolean },
    lines: [] as QuoLine[],
    thread: { pms: null as any, sms: null as any }, threadErr: { pms: '', sms: '' }, threadBusy: { pms: false, sms: false },
    draft: '', sending: false,
    // replies we sent this session that the re-read thread may not show yet (Hostaway / Quo lag)
    pending: { pms: [] as { body: string; at: string }[], sms: [] as { body: string; at: string }[] },
  };
  const html = `
    <div class="gx-sheet gx-msgs" data-gx-sheet="message">
      <div class="gx-ctx">💬 <b>${esc(t.guest || 'Guest')}</b> · ${esc(t.listing_name)}${t.check_in ? ` · ${esc(stayLine(ctx, t.check_in, t.check_out))}` : ''}</div>
      <div data-gx-tabs hidden></div>
      <div class="gx-thread" data-gx-thread aria-live="polite"><div class="gx-load">Finding this booking's conversation…</div></div>
      <div class="gx-field">
        <div class="gx-chline" data-gx-chline></div>
        <div class="gx-off" data-gx-off hidden></div>
        <textarea class="input" data-gx-draft rows="3" maxlength="2000" placeholder="Type a reply…" disabled></textarea>
      </div>
      <div class="gx-note" data-gx-note hidden></div>
      <div class="gx-foot" data-bottom-bar>
        <button type="button" class="btn btn-ghost gx-foot-left" data-gx-open-msg>Open in Messaging</button>
        <button type="button" class="btn btn-primary" data-gx-send disabled>Send ▸</button>
      </div>
    </div>`;
  const st = startSheet(ctx, 'message', t.hid, 'Message the guest', html);
  const root = st.host;
  if (!root) return;
  st.cleanup.push(() => { pmsConfirm.close(); s.thread = { pms: null, sms: null }; s.pending = { pms: [], sms: [] }; s.draft = ''; s.by = null; s.sms = null; });
  const cid = () => (s.by && s.by.conversation_id) || null;
  const comp = () => composerState(s.tab, s.tab === 'pms' ? { cid: cid(), sendOn: !!(s.by && s.by.pms_send) } : { line: s.sms ? s.sms.phone_number_id : null, to: s.sms ? s.sms.participants[0] : null });
  const drawTabs = () => {
    const el = $(root, '[data-gx-tabs]');
    if (!el) return;
    const both = !!cid() && !!s.sms;
    el.hidden = !both;
    el.innerHTML = both ? `<div class="seg-tabs gx-seg" role="tablist"><button type="button" class="seg-tab ${s.tab === 'pms' ? 'active' : ''}" data-gx-tab="pms" role="tab">${esc(chanLabel)} thread</button><button type="button" class="seg-tab ${s.tab === 'sms' ? 'active' : ''}" data-gx-tab="sms" role="tab">${s.sms && s.sms.fresh ? 'SMS (new)' : 'SMS'}</button></div>` : '';
    el.querySelectorAll<HTMLElement>('[data-gx-tab]').forEach(b => b.addEventListener('click', () => { s.tab = b.dataset.gxTab as 'pms' | 'sms'; drawTabs(); drawThread(); drawComposer(); void loadThread(s.tab); }));
  };
  const drawThread = () => {
    if (st.closed) return;
    const el = $(root, '[data-gx-thread]');
    if (!el) return;
    if (s.loading) { el.innerHTML = '<div class="gx-load">Finding this booking\'s conversation…</div>'; return; }
    if (s.error) { el.innerHTML = `<div class="gx-err" role="alert" style="margin:8px;">${esc(s.error)}</div>`; return; }
    const tab = s.tab;
    // R115 review: a failed Hostaway lookup is said as such (never "no conversation"), with a retry
    const lookupFailed = !cid() && !!(s.by && s.by.lookup_error);
    if (lookupFailed && (tab === 'pms' || !s.sms)) {
      el.innerHTML = `<div class="gx-err" role="alert" style="margin:8px;">Couldn't reach Hostaway to find this booking's thread — ${esc(String(s.by!.lookup_error).slice(0, 160))}</div><div class="gx-btnrow" style="padding:0 8px 8px;"><button type="button" class="btn btn-secondary btn-sm" data-gx-relookup>↻ Retry</button></div>`;
      $(el, '[data-gx-relookup]')?.addEventListener('click', () => { s.loading = true; s.error = ''; s.by = null; s.sms = null; convRefs.drop(t.hid); drawTabs(); drawThread(); drawComposer(); void load(); });
      return;
    }
    if (!cid() && !s.sms) {
      const cfg = s.by && s.by.configured;
      el.innerHTML = `<div class="gx-faint">${cfg && !cfg.pms && !cfg.sms ? "Messaging isn't connected on this server (no Hostaway / Quo keys)." : 'No conversation found for this booking yet — open Messaging to start one, or reply in Hostaway.'}</div>`;
      return;
    }
    if (tab === 'sms' && s.sms && s.sms.fresh && !(s.thread.sms && Array.isArray(s.thread.sms.messages) && s.thread.sms.messages.length) && !s.threadBusy.sms && !s.threadErr.sms) {
      el.innerHTML = `<div class="gx-faint">No texts with ${esc(t.guest || 'this guest')} yet — your first message starts an SMS thread from the line below.</div>`;
      return;
    }
    if (s.threadBusy[tab] && !s.thread[tab]) { el.innerHTML = '<div class="gx-load">Loading messages…</div>'; return; }
    if (s.threadErr[tab]) { el.innerHTML = `<div class="gx-err" role="alert" style="margin:8px;">${esc(s.threadErr[tab])}</div><div class="gx-btnrow" style="padding:0 8px 8px;"><button type="button" class="btn btn-secondary btn-sm" data-gx-reload>↻ Retry</button></div>`; $(el, '[data-gx-reload]')?.addEventListener('click', () => void loadThread(tab, true)); return; }
    const th = s.thread[tab];
    const msgs = th && Array.isArray(th.messages) ? th.messages : [];
    const v = threadView(msgs, 30);
    el.innerHTML = `${v.older ? `<div class="gx-faint" style="text-align:center;">${v.older} older message${v.older === 1 ? '' : 's'} — <button type="button" class="gx-link" data-gx-open-msg2>open in Messaging</button></div>` : ''}
      ${v.shown.length ? v.shown.map((m: any) => {
        const out = m.direction === 'outgoing';
        const body = String(m.body || m.text || '');
        const imgs = Array.isArray(m.images) ? m.images.length : (Array.isArray(m.media) ? m.media.length : 0);
        return `<div class="gx-bub ${out ? 'out' : 'in'}">${esc(body)}${imgs ? `${body ? '<br>' : ''}<span class="gx-faint">📎 ${imgs} image${imgs === 1 ? '' : 's'}</span>` : ''}<span class="gx-when">${out ? 'Us' : esc(t.guest || 'Guest')} · ${esc(fmtWhen(m.at || m.created_at))}</span></div>`;
      }).join('') : '<div class="gx-faint">No messages on this thread yet.</div>'}`;
    $(el, '[data-gx-open-msg2]')?.addEventListener('click', openMessaging);
    el.scrollTop = el.scrollHeight;
  };
  const drawComposer = () => {
    if (st.closed) return;
    const c = comp();
    const ta = $<HTMLTextAreaElement>(root, '[data-gx-draft]');
    const off = $(root, '[data-gx-off]');
    const line = $(root, '[data-gx-chline]');
    const send = $<HTMLButtonElement>(root, '[data-gx-send]');
    if (line) line.innerHTML = s.loading ? '' : s.tab === 'pms'
      ? (cid() ? `Replying via <b>PMS</b> · ${esc(chanLabel)} thread (through Hostaway) — the guest sees it in the booking channel` : '')
      : (s.sms ? (s.sms.fresh
        ? `New <b>SMS</b> to ${esc(maskPhone(s.sms.participants[0]))} from ${s.lines.length > 1 ? `<select class="input gx-linepick" data-gx-line aria-label="Send from line">${s.lines.map(l => `<option value="${esc(l.id)}"${l.id === s.sms!.phone_number_id ? ' selected' : ''}>${esc(l.name || l.number)}</option>`).join('')}</select>` : `<b>${esc(s.sms.line)}</b>`}`
        : `Replying via <b>SMS</b>${s.sms.line ? ` · ${esc(s.sms.line)}` : ''}`) : '');
    const lp = line && line.querySelector<HTMLSelectElement>('[data-gx-line]');
    if (lp) lp.addEventListener('change', () => { const l = s.lines.find(x => x.id === lp.value); if (l && s.sms) { s.sms.phone_number_id = l.id; s.sms.line = l.name || l.number; } });
    const readOnly = !s.loading && !c.canSend && !!(s.tab === 'pms' ? cid() : s.sms);
    if (off) { off.hidden = !readOnly; off.textContent = readOnly ? `${c.reason}${s.tab === 'pms' && s.sms ? ' — or use the SMS tab above.' : ''}` : ''; }
    if (ta) { ta.disabled = !c.canType || s.sending; ta.maxLength = c.max || 2000; if (ta.value !== s.draft) ta.value = s.draft; }
    if (send) { send.disabled = !c.canSend || s.sending || !s.draft.trim(); send.textContent = s.sending ? 'Sending…' : (c.confirm ? 'Review & send ▸' : 'Send ▸'); }
  };
  const loadThread = async (tab: 'pms' | 'sms', force = false) => {
    if (s.threadBusy[tab] || (!force && s.thread[tab])) return;
    let url = '';
    if (tab === 'pms') { if (!cid()) return; url = `/api/messaging/thread?channel=pms&id=${encodeURIComponent(cid()!)}&reservation_id=${encodeURIComponent(t.hid)}`; }
    else { if (!s.sms) return; url = `/api/messaging/thread?channel=sms&phone_number_id=${encodeURIComponent(s.sms.phone_number_id)}&participants=${encodeURIComponent(s.sms.participants.join(','))}`; }
    s.threadBusy[tab] = true; s.threadErr[tab] = ''; drawThread();
    const r = await getJson(url);
    s.threadBusy[tab] = false;
    if (st.closed) return;
    if (r.ok && r.json && r.json.configured !== false) {
      const msgs: any[] = Array.isArray(r.json.messages) ? r.json.messages : [];
      // keep a just-sent reply visible until the re-read thread carries it
      s.pending[tab] = s.pending[tab].filter(p => !msgs.some(m => m && m.direction === 'outgoing' && String(m.body || m.text || '').trim() === p.body));
      r.json.messages = [...msgs, ...s.pending[tab].map(p => ({ kind: 'message', channel: tab, direction: 'outgoing', body: p.body, at: p.at, images: [] }))];
      s.thread[tab] = r.json;
    } else s.threadErr[tab] = r.ok ? 'This channel is not connected on this server.' : errText(r, "Couldn't load the messages");
    if (s.tab === tab) drawThread();
  };
  const load = async () => {
    const r = await getJson<ByRes>(`/api/messaging/by-reservation/${encodeURIComponent(t.hid)}`);
    if (st.closed) return;
    s.loading = false;
    if (!r.ok || !r.json) { s.error = errText(r, "Couldn't look up this booking's conversation"); drawThread(); drawComposer(); return; }
    s.by = r.json;
    s.tab = r.json.conversation_id ? 'pms' : 'sms';
    // SMS: only when Quo is connected and the booking has a phone (Airbnb usually masks it)
    if (r.json.configured && r.json.configured.sms && r.json.guest && r.json.guest.phone) {
      const q = await getJson(`/api/quo/find?phone=${encodeURIComponent(r.json.guest.phone)}`);
      if (st.closed) return;
      const fd = q.ok && q.json && q.json.found;
      if (fd && fd.phone_number_id) s.sms = { phone_number_id: String(fd.phone_number_id), participants: Array.isArray(fd.participants) && fd.participants.length ? fd.participants.map(String) : [r.json.guest.phone], line: String(fd.line || '') };
      // R115 review (B5): no Quo thread with this guest yet → offer a NEW one (Messaging's "New text", same /send route).
      // Only when every line answered — a line that errored might hold the real thread (no duplicate threads).
      else if (q.ok && q.json && q.json.configured !== false && !fd && !(Array.isArray(q.json.errors) && q.json.errors.length)) {
        const L = await getJson<{ lines: QuoLine[] }>('/api/quo/lines');
        if (st.closed) return;
        const lines = L.ok && L.json && Array.isArray(L.json.lines) ? L.json.lines.filter(l => l && l.id).map(l => ({ id: String(l.id), number: String(l.number || ''), name: String(l.name || '') })) : [];
        if (lines.length) {
          let pref = '';
          try { pref = sessionStorage.getItem('qx_line') || ''; } catch { pref = ''; }
          const line = lines.find(l => l.id === pref) || lines[0];
          s.lines = lines;
          s.sms = { phone_number_id: line.id, participants: [r.json.guest.phone], line: line.name || line.number, fresh: true };
        }
      }
    }
    if (!cid() && s.sms) s.tab = 'sms';
    drawTabs(); drawThread(); drawComposer();
    void loadThread(s.tab);
  };
  const openMessaging = () => {
    const phone = s.by && s.by.guest ? s.by.guest.phone : null;
    const c = cid();
    closeSheet(ctx, st);
    handoff.messaging(ctx, { cid: c, rid: t.hid, phone });
  };
  const afterSend = (tab: 'pms' | 'sms', body: string) => {
    const th = s.thread[tab];
    const at = new Date().toISOString();
    s.pending[tab].push({ body, at });
    if (th && Array.isArray(th.messages)) th.messages.push({ kind: 'message', channel: tab, direction: 'outgoing', body, at, images: [] });
    s.draft = ''; setDirty(ctx, st, false);
    if (tab === 'sms' && s.sms) s.sms.fresh = false;   // the thread exists now
    dropPreview(t.hid);   // the stay panel's preview re-reads the thread
    note(root, tab === 'pms' ? "Sent to the guest's PMS thread." : 'Sent by SMS.', 'ok');
    drawThread(); drawComposer();
    ctx.toast('Message sent.');
    void loadThread(tab, true);
  };
  const failSend = (r: { status: number; json: any }, where: string) => {
    if (r.status === 503 && r.json && r.json.pms_send === false && s.by) s.by.pms_send = false;
    note(root, `Send failed — ${(r.json && r.json.error) || (r.status ? `HTTP ${r.status}` : 'the network did not answer')}${maybeSent(r.status, r.json) ? ` (it may still have gone out — check the thread${where} before resending)` : ''}`);
    drawComposer();
  };
  const sendPms = async () => {
    const body = s.draft.trim();
    if (!body || s.sending || !cid()) return;
    s.sending = true; st.busy = true; pmsConfirm.setBusy(true);
    const r = await postJson('/api/messaging/send', { channel: 'pms', conversation_id: cid(), body, confirm: true });
    s.sending = false; st.busy = false; pmsConfirm.close();
    if (st.closed) return;
    if (!r.ok) { failSend(r, ' in Hostaway'); return; }
    afterSend('pms', body);
  };
  const sendSms = async () => {
    const body = s.draft.trim();
    if (!body || s.sending || !s.sms) return;
    s.sending = true; st.busy = true; note(root, ''); drawComposer();
    const r = await postJson('/api/messaging/send', { channel: 'sms', from: s.sms.phone_number_id, to: s.sms.participants, content: body });
    s.sending = false; st.busy = false;
    if (st.closed) return;
    if (!r.ok) { failSend(r, ''); return; }
    afterSend('sms', body);
  };
  $<HTMLTextAreaElement>(root, '[data-gx-draft]')?.addEventListener('input', (e) => {
    s.draft = (e.target as HTMLTextAreaElement).value;
    setDirty(ctx, st, !!s.draft.trim());
    const send = $<HTMLButtonElement>(root, '[data-gx-send]');
    if (send) send.disabled = !comp().canSend || s.sending || !s.draft.trim();
  });
  $(root, '[data-gx-send]')?.addEventListener('click', () => {
    const c = comp();
    if (!c.canSend || !s.draft.trim()) return;
    note(root, '');
    if (s.tab === 'sms') { void sendSms(); return; }
    // a PMS reply posts to the guest's channel thread — ALWAYS through the confirm step
    pmsConfirm.open({ channel: chanLabel, guest: t.guest || (s.by && s.by.guest && s.by.guest.name) || 'Guest', property: t.listing_name, stay: stayLine(ctx, t.check_in, t.check_out) || undefined, body: s.draft },
      { onSend: () => sendPms(), watch: document.body, isAlive: () => !st.closed, zIndex: 350 });
  });
  $(root, '[data-gx-open-msg]')?.addEventListener('click', openMessaging);
  drawComposer();
  void load();
}
